interface CalculationParams {
  valorImovel: number;
  valorAluguel: number;
  valorizacao: number;
  igpm: number;
  entrada: number;
  custos: number;
  prazo: number;
  taxaAnual: number;
  rentabilidade: number;
}

export function calculateRentVsBuy(params: CalculationParams) {
  const {
    valorImovel,
    valorAluguel,
    valorizacao,
    igpm,
    entrada,
    custos,
    prazo,
    taxaAnual,
    rentabilidade,
  } = params;

  const valorFinanciar = valorImovel - entrada;
  const amortizacao = valorFinanciar / prazo;
  const jurosAoMes = Math.pow(1 + taxaAnual / 100, 1 / 12) - 1;
  const rentabilidadeMensal = Math.pow(1 + rentabilidade / 100, 1 / 12) - 1;

  let saldoDevedor = valorFinanciar;
  let patrimonioFinanciando = 0;
  let patrimonioAlugando = entrada + custos; // Initialize with entrada + custos
  let totalAluguel = 0;
  let aluguelAtual = valorAluguel;
  let valorFinalFinanciamento = 0;

  const meses: number[] = [];
  const prestacoes: number[] = [];
  const alugueis: number[] = [];
  const saldosDevedores: number[] = [];
  const aportesFinanciando: number[] = [];
  const aportesAlugando: number[] = [];

  // Initialize month 0 with starting values
  meses.push(0);
  prestacoes.push(entrada + custos);
  alugueis.push(valorAluguel);
  saldosDevedores.push(valorFinanciar);
  aportesFinanciando.push(0);
  aportesAlugando.push(entrada + custos);

  // Start the loop from month 1
  for (let i = 1; i <= prazo; i++) {
    meses.push(i);

    // Cálculo do aluguel
    if (i % 12 === 0 && i >= 1) {
      aluguelAtual *= 1 + igpm / 100;
    }
    totalAluguel += aluguelAtual;
    alugueis.push(aluguelAtual);

    // Cálculo do financiamento
    const juros = saldoDevedor * jurosAoMes;
    const prestacao = amortizacao + juros;
    saldoDevedor -= amortizacao;

    prestacoes.push(prestacao);
    saldosDevedores.push(saldoDevedor);

    // Cálculo dos investimentos
    const investimentoDoMesAlugando = Math.max(0, prestacao - aluguelAtual);
    const investimentoDoMesFinanciando = Math.max(0, aluguelAtual - prestacao);

    aportesAlugando.push(investimentoDoMesAlugando);
    aportesFinanciando.push(investimentoDoMesFinanciando);

    // Atualizar patrimônio alugando com rentabilidade
    patrimonioAlugando = patrimonioAlugando * (1 + rentabilidadeMensal);
    patrimonioAlugando += investimentoDoMesAlugando;

    // Atualizar patrimônio financiando com rentabilidade
    patrimonioFinanciando = patrimonioFinanciando * (1 + rentabilidadeMensal);
    patrimonioFinanciando += investimentoDoMesFinanciando;

    valorFinalFinanciamento += prestacao;
  }

  const totalImovel = valorImovel * Math.pow(1 + valorizacao / 100, prazo / 12);
  valorFinalFinanciamento += custos + entrada;

  const patrimonioTotalFinanciando = patrimonioFinanciando + totalImovel;
  const totalAportesFinanciando = aportesFinanciando.reduce((a, b) => a + b, 0);
  const totalAportesAlugando = aportesAlugando.reduce((a, b) => a + b, 0);

  const bestChoice =
    patrimonioTotalFinanciando > patrimonioAlugando
      ? "Financiar e investir"
      : "Alugar e investir";

  return {
    imovel: totalImovel.toFixed(2),
    totalAluguel: totalAluguel.toFixed(2),
    financiamento: valorFinalFinanciamento.toFixed(2),
    patrimonioFinanciando: patrimonioTotalFinanciando.toFixed(2),
    patrimonioAlugando: patrimonioAlugando.toFixed(2),
    prazo,
    bestChoice,
    meses,
    prestacoes,
    alugueis,
    saldosDevedores,
    aportesFinanciando,
    aportesAlugando,
    totalAportesFinanciando: totalAportesFinanciando.toFixed(2),
    totalAportesAlugando: totalAportesAlugando.toFixed(2),
    rentabilidade,
    valorizacao,
    igpm,
  };
}
